import React, { useState, useRef, useCallback } from 'react';
import { territoriesApi } from '../services/api';

export default function PhotoUpload({ territoryId, onUploaded }) {
  const [dragging, setDragging]   = useState(false);
  const [files, setFiles]         = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState('');
  const inputRef = useRef();

  const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SIZE  = 10 * 1024 * 1024; // 10 MB

  // ── accepts a FileList, a File[], or a single File ──────────────────────────
  const addFiles = useCallback((input) => {
    const list = input instanceof FileList
      ? Array.from(input)
      : Array.isArray(input)
        ? input
        : [input];                     // ← was the bug: single File passed here

    const valid = list.filter(f => {
      if (!f) return false;
      if (!ACCEPTED.includes(f.type)) {
        setError(`Format non supporté : ${f.name}`);
        return false;
      }
      if (f.size > MAX_SIZE) {
        setError(`Fichier trop lourd (max 10 Mo) : ${f.name}`);
        return false;
      }
      return true;
    });

    if (valid.length) setError('');
    setFiles(prev => [...prev, ...valid].slice(0, 10));
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);     // FileList — handled correctly now
  }, [addFiles]);

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('images', f));

      const interval = setInterval(() => setProgress(p => Math.min(p + 15, 85)), 200);
      const result   = await territoriesApi.uploadPhotos(territoryId, formData);
      clearInterval(interval);
      setProgress(100);

      setTimeout(() => {
        setFiles([]);
        setProgress(0);
        setUploading(false);
        if (onUploaded) onUploaded(result.images || []);
      }, 600);
    } catch (err) {
      setUploading(false);
      setProgress(0);
      setError(err.message || "Erreur lors de l'upload");
    }
  };

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Drop zone */}
      <div
        onDragEnter={e => { e.preventDefault(); setDragging(true); }}
        onDragOver={e  => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border     : `2px dashed ${dragging ? '#E8532A' : 'rgba(140,122,106,0.35)'}`,
          borderRadius: 4,
          padding    : '40px 24px',
          textAlign  : 'center',
          cursor     : 'pointer',
          background : dragging ? 'rgba(232,83,42,0.04)' : '#FAFAF8',
          transition : 'all 0.2s',
          marginBottom: 16,
        }}
      >
        {/* ── key fix: pass the full FileList, not files[0] ── */}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)}
        />
        <div style={{ fontSize: 32, marginBottom: 10 }}>📸</div>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>Glissez vos photos ici</p>
        <p style={{ fontSize: 13, color: '#8C7A6A' }}>
          ou cliquez pour sélectionner · JPEG, PNG, WebP · max 10 Mo · max 10 images
        </p>
      </div>

      {/* Previews */}
      {files.length > 0 && (
        <div style={{
          display            : 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap                : 8,
          marginBottom       : 16,
        }}>
          {files.map((file, i) => {
            const url = URL.createObjectURL(file);
            return (
              <div key={i} style={{ position: 'relative', borderRadius: 4, overflow: 'hidden', aspectRatio: '1' }}>
                <img
                  src={url}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onLoad={() => URL.revokeObjectURL(url)}
                />
                {!uploading && (
                  <button
                    onClick={e => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      position: 'absolute', top: 4, right: 4,
                      width: 22, height: 22, borderRadius: '50%',
                      background: 'rgba(26,18,8,0.7)', border: 'none',
                      color: '#fff', cursor: 'pointer', fontSize: 12,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >✕</button>
                )}
                <div style={{
                  position  : 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'rgba(26,18,8,0.5)', padding: '2px 4px',
                }}>
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', margin: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {file.name}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress */}
      {uploading && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 13, color: '#5A4A3C' }}>Upload en cours...</span>
            <span style={{ fontSize: 13, fontFamily: 'Space Mono, monospace', color: '#E8532A' }}>{progress}%</span>
          </div>
          <div style={{ height: 4, background: '#F0EBE0', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#E8532A', borderRadius: 2, width: `${progress}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}

      {error && <p style={{ color: '#E8532A', fontSize: 13, marginBottom: 12 }}>⚠️ {error}</p>}

      {files.length > 0 && !uploading && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={upload}
            style={{
              background  : '#E8532A', color: '#fff', border: 'none',
              padding     : '10px 24px', borderRadius: 2, cursor: 'pointer',
              fontFamily  : 'Space Mono, monospace', fontSize: 12,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}
          >
            Envoyer {files.length} photo{files.length > 1 ? 's' : ''} →
          </button>
          <button
            onClick={() => setFiles([])}
            style={{
              background: 'none', border: '1px solid rgba(140,122,106,0.3)',
              padding   : '10px 18px', borderRadius: 2, cursor: 'pointer',
              fontFamily: 'Space Mono, monospace', fontSize: 11, color: '#8C7A6A',
            }}
          >
            Effacer
          </button>
        </div>
      )}
    </div>
  );
}